<template>
  <div id="app">
    <help-hub-header
      :env="environment"
      :title="'Employee Hub'"
      @onHHComponentSuccess="onHHComponentSuccess"
      @onHHComponentError="onHHComponentError"
    />
    <div :class="{ 'black-background' : !isLoading && currentAppData}">
 
      <help-hub-breadcrumb v-if="!isLoading&&currentAppData" :title="currentAppData.name"></help-hub-breadcrumb>
     <div class="container px-0">
        <div v-if="!isLoading && currentAppData" class="search-container h-100">
          <div class="row m-0" v-if="authToken">
            <div class="col-md-10 pb-md-0 pt-md-0">  
                <div class="breadcrumb-desc">{{currentAppData.description}}</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- use simple search -->
      <div class="row m-0 mt-lg-0 mt-3 simple-search" v-if="authToken && currentAppData && !currentAppData.enableEnterpriseSearch ">
        <div class="col-xl-5 col-lg-6 col-md-9 col-12">
          <div class="row pb-4 ml-0 ">
            <div class="col-md-12 p-0">
              <div class="input-group-prepend search-input-container pr-5">
                <span class="input-group-text">
                  <img
                    v-if="showSearchSpinner"
                    class="search-loading-spinner"
                    src="@/assets/images/loading.gif"
                  />
                  <img v-else src="@/assets/images/icon_search.svg" />
                </span>

                <div class="input-container w-100">
                  <input
                    type="text"
                    v-model="searchTerm"
                    placeholder="Search Legal Marketing Toolkit"
                    class="form-control"
                  />
                </div>
                <button v-if="searchTerm" class="close close-btn mr-1" @click="clearSearchTerm">
                  <font-awesome-icon :icon="['far', 'times-circle']" size="lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- use enterprise search -->
      <div class="search-wrapper" :class="[ showGuidesContent ? 'search-padding' : 'mb-4']">
        <div class="row" v-if="!isLoading && authResponse && currentAppData && currentAppData.enableEnterpriseSearch">
          <div class="col" v-show="isSearchReady">
            <enterprise-search
                :env="environment"
                :es-title="'Employee Hub - Guides search'"
                :es-user-access-token="authResponse.accessToken"
                :es-user-id-token="authResponse.idToken"
                :user-id="authResponse.user.userName"
                :user-email="authResponse.user.email"
                :enable-url-params="false"
                @onWebComponentSuccess="handleESComponentReady"
                @onWebComponentError="handleESComponentReady"
                @onSearchKeywordChanged="handleSearchKeywordChanged"
                :key="componentKey"
                :input-placeholder="searchPlaceholder"
                :customize-search="true"
                :enable-employee-hub-search="true"
                :query-overrides.prop="getQueryOverrides()"
                :popular-searches-alias="currentAppData.alias"
            ></enterprise-search>
          </div>
        </div>
      </div>
    </div>

    <modals-container />
    <loading-spinner v-if="!authToken"></loading-spinner>

    <div v-if="authToken && !isLoading">
      <div class="container-fluid main-container">
        <div class="offset-md-1" :class="[currentAppData && !currentAppData.enableEnterpriseSearch && searchTerm ? 'col-xl-5 col-lg-6 col-md-9 col-12': 'col-md-10']">
          <div v-if="currentAppData && !currentAppData.enableEnterpriseSearch && searchTerm">
            <search-results :search-term="searchTerm" @clearSearchTerm="clearSearchTerm"></search-results>
          </div>
          <router-view v-if="showGuidesContent" :key="$route.fullPath"></router-view>
        </div>
      </div>
    </div>
    <help-hub-footer :env="environment" v-if="!isLoading" />
  </div>
</template>

<script>
import axios from "axios";
import Vue from "vue";
import LoadingSpinner from "./components/LoadingSpinner";
import CONSTANTS from "@/store/constants";
import { mapGetters, mapMutations } from "vuex";
import SearchResults from "./components/SearchResults";

export default {
  name: "app",
  components: { LoadingSpinner, SearchResults },
  data() {
    return {
      environment: CONSTANTS.ENV,
      authToken: null,
      isLoading: true,
      inputSearchText: null,
      searchTerm: null,
      enterpriseSearchTerm: null,
      componentKey: 0,
      isSearchReady: false,
    };
  },
  computed: {
    ...mapGetters([
      "currentAppData",
      "currentAlias",
      "allCategories",
      "currentUser",
      "showSearchSpinner",
      "authResponse"
    ]),
    showGuidesContent() {
      let invalidAlias = !this.currentAppData;
      // Don't include this redirectUrl changes here in the new-guides-master branch.
      let noSearchResults = this.currentAppData && !this.currentAppData.enableEnterpriseSearch && !this.searchTerm;
      let noEnterpriseSearchResults = this.currentAppData && this.currentAppData.enableEnterpriseSearch && !this.enterpriseSearchTerm;
      return invalidAlias || noSearchResults || noEnterpriseSearchResults;
    },
    searchPlaceholder() {
      let placeholder = "Search";
      if (this.currentAppData && this.currentAppData.name) {
        return `${placeholder} ${this.currentAppData.name}`
      }
      return placeholder
    }
  },
  created() {
    this.$nextTick(() => {
      this.componentKey++;
    });
  },
  methods: {
    ...mapMutations(["setAuthResponse"]),
    clearSearchTerm() {
      this.searchTerm = null;
    },
    getQueryOverrides() {
      if (!this.currentAppData || !this.currentAppData.enterpriseSearchQuery) return null;

      let overrides = {
        template: this.currentAppData.enterpriseSearchQuery,
        length: this.currentAppData.enterpriseSearchResultsLength
      };

      return overrides;
    },

    onHHComponentSuccess(response) {
      response = response.detail[0];
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + response.accessToken;
      axios.defaults.baseURL = CONSTANTS.EMPLOYEE_HUB_API_BASE_URL
      this.authToken = response.accessToken;
      this.setAuthResponse(response);
      var mixpanelInstance = response.mixpanelInstance;
      // Only initialize mixpanel once
      Vue.mixin({
        created: function() {
          this.CONSTANTS = CONSTANTS;
        }
      });
      if (mixpanelInstance && !Vue.prototype.$hhMixpanel) {
        Vue.prototype.$hhMixpanel = mixpanelInstance;
      }
      this.isLoading = false;
    },


    onHHComponentError() {
      this.isLoading = false;
    },
    handleSearchKeywordChanged(response) {
      let existsSearchTerm = response && response.detail && response.detail[0];
      this.enterpriseSearchTerm = (existsSearchTerm) ? response.detail[0] : null;
    },
    handleESComponentReady() {
      this.isSearchReady = true;
    },

  }
};
</script>
<style scoped>
.search-container .input-group-text {
  border-right: none;
  border-top-left-radius: 25px;
  border-bottom-left-radius: 25px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  color: #000000;
  padding-right: 5px;
  cursor: pointer;
  opacity: 0.8;
}

.search-container {
  margin-top: -2px;
}
.input-group-text {
  display: flex;
  -ms-flex-align: center;
  -webkit-box-align: center;
  align-items: center;
  padding: 0.375rem 0.77rem;
  margin-bottom: 0;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  white-space: nowrap;
}
.search-container input {
  border-radius: 25px;
  border-left: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  font-size: 16px;
  padding-left: 8px;
  background: #ffffff;
  color: #000000;
}

.search-container input::placeholder {
  color: #000000;
}

.search-input-container {
  margin-left: 30px;
  margin-right: 15px;
  height: 100%;
  border: 1px solid #000000;
  border-left: none !important;
  background-color: #ffffff !important;
}
.search-input-container .form-control {
  height: 100%;
  border: 0px !important;
}
.search-input-container .form-control:focus {
  border-color: #ced4da !important;
  box-shadow: none !important;
}

.input-container input::-ms-clear {
  display: none;
  width: 0;
  height: 0;
}
.input-container input::-ms-reveal {
  display: none;
  width: 0;
  height: 0;
}

.breadcrumb-desc {
  color: #000000;
  font-size: 20px;
  margin-top: -35px;
  margin-left: -3px;
}
.search-loading-spinner {
  width: 25px;
  height: 25px;
}

/* search adjustments */
.search-padding {
  padding-bottom: 34px;
}

.search-wrapper >>> .container {
  max-width: unset !important;
}
.search-wrapper >>> .search-input-wrapper {
  max-width: 1320px !important;
  margin-left: auto !important;
  margin-right: auto !important;
}
@media (min-width: 992px) {
  .search-wrapper >>>   .input-group {
    max-width: 50% !important;
  }
}
@media (min-width: 1320px) {
  .search-wrapper >>> .search-input-wrapper {
    padding-left: 15px !important;
    padding-right: 15px !important;
  }
}
.search-wrapper >>> .search-card-container,
.search-wrapper >>> .nav-bar-wrapper > .col-md-6 {
  max-width: 1320px !important;
  margin-left: auto !important;
  margin-right: auto !important;
  flex: 0 0 100% !important;
}



/* hide enterprise search feedback button in guides */
.search-wrapper >>> .feedback-list {
  display: none;
}
.search-wrapper >>> .explore-es-text {
  display: none;
}
.search-wrapper >>> .search-error-container {
  background: rgb(237, 239, 240);
}

.simple-search >>> .input-group-text {
  background: none !important;
  border: none !important;
}
.black-background {
  background: #000000 !important;
}

.search-wrapper >>>  .search-icon-pos {
   top: 9px !important;
}
.search-wrapper >>> .remove-icon-btn-pos {
  top: 15px !important;
}
.search-wrapper >>> .autocomplete-parent-container {
  padding-left: 20px;
  padding-right: 20px;
}
</style>

<style>

.popular-searches-text {
  color: #ffffff !important;

}

.breadcrumb-title {
  font-size: 36px !important;
  color: #ffffff !important;
}

.breadcrumb-desc {
  font-size: 21px !important;
  color: #ffffff !important;
  font-family: artifaktElementBold !important;
}


.search-input {
  opacity: 1.0 !important;
  border-radius: 25px !important;
  height: 100% !important;
  font-size: 18px !important;
  font-family: artifaktElementRegular !important;
  font-style: italic;
  line-height: 18px !important;
}

</style>
